import React, { useEffect, useState } from "react";
import { TitleBar } from "../../Components/Common/TitleBar/titleBar";
import * as Yup from "yup";
import {
  Button,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Spinner,
  Table,
  Toast,
  ToastBody,
  ToastHeader,
} from "reactstrap";
import "./style.scss";
import { TripModal } from "../../Components/Common/TripModal/tripModal";
import DeleteModal from "../../Components/Common/DeleteModal";
import { Pagination } from "../../Components/Common/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  createVehicle,
  deleteVehicle,
  getAllVehicleList,
  getVehicleById,
  getVehicleType,
  updateVehicle,
} from "../../slices/vehicleManagement/thunk";
import { useRef } from "react";
import { Formik } from "formik";
import {
  hideErrorMessage,
  setLoading,
} from "../../slices/vehicleManagement/reducer";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const VehicleManagement = () => {
  const formikRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const vehicleList = useSelector((state) => state.Vehicle.vehicleList);
  const vehicleType = useSelector((state) => state.Vehicle.vehicleByType);
  const vehicleById = useSelector((state) => state.Vehicle.vehicleById);
  const loading = useSelector((state) => state.Vehicle.loading);

  const [addVehicleModal, setAddVehicleModal] = useState(false);
  const [deleteVehicleModal, setDeleteVehicleModal] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [toasterType, setToasterType] = useState("");

  const toggleAddVehicleModal = async () => {
    setAddVehicleModal(!addVehicleModal);
  };

  const toggleDeleteVehicleModal = async () => {
    setDeleteVehicleModal(!deleteVehicleModal);
  };

  const handleNavigate = (id) => {
    navigate(`/vehicle-details/${id}`);
  };

  const vehicleSchema = Yup.object().shape({
    alias: Yup.string().required("Alias is required"),
    model: Yup.string().required("Vehicle Model is required"),
    year: Yup.string()
      .matches(/^\d{4}$/, "Year must be a 4-digit number")
      .required("Vehicle Year is required"),
    vehicle_type: Yup.string().required("Vehicle Type is required"),
    number_plate: Yup.string().required("Vehicle Number is required"),
    company_ownership: Yup.string().required(
      "Vehicle Company Ownership is required"
    ),
  });

  const addInitialValues = {
    name: "",
    vehicle_type: "",
    number_plate: "",
    alias: "",
    company_ownership: "",
    model: "",
    year: "",
    status: "active",
  };

  const updateInitialValues = {
    name: vehicleById.name || "",
    vehicle_type: vehicleById.vehicle_type?.id || null,
    number_plate: vehicleById.number_plate || "",
    alias: vehicleById.alias || "",
    company_ownership: vehicleById.company_ownership || "",
    model: vehicleById.model || "",
    year: vehicleById.year,
    status: vehicleById.status || "active",
  };

  const showToaster = (isSuccess, message) => {
    setShowToast(true);
    setToasterType(isSuccess ? "success" : "danger");
    setToastMessage(message);
    setTimeout(() => {
      setShowToast(false);
      if (!isSuccess) {
        dispatch(hideErrorMessage());
      }
    }, 3000);
  };

  const handleVehicleSubmit = async (values) => {
    try {
      let response;

      if (!isAdd) {
        console.log("vehicleById.id", vehicleById.id);

        response = await dispatch(updateVehicle(vehicleById.id, values));
      } else {
        response = await dispatch(createVehicle(values));
      }
      const { success: isSuccess, message } = response;
      showToaster(isSuccess, message);
    } catch (error) {
      showToaster(false, error.message || "An unexpected error occurred");
    } finally {
      toggleAddVehicleModal();
      await dispatch(getAllVehicleList(currentPage, pageSize, searchText));
    }
  };

  const handleDeleteVehicle = async () => {
    try {
      const { success: isSuccess, message } = await dispatch(
        deleteVehicle(vehicleById.id)
      );
      if (isSuccess) {
        showToaster(isSuccess, message);
      }
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      showToaster(
        false,
        error?.message ||
        "An unexpected error occurred while deleting the vehicle"
      );
    } finally {
      toggleDeleteVehicleModal();
      dispatch(getAllVehicleList(currentPage, pageSize, searchText));
    }
  };

  useEffect(() => {
    try {
      dispatch(getVehicleType());
      const delayDebounceFn = setTimeout(() => {
        dispatch(getAllVehicleList(currentPage, pageSize, searchText));
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    } catch (error) { }
  }, [dispatch, currentPage, pageSize, searchText]);

  document.title = "Vehicle Management | D&A";
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex flex-column gap-4">
            <TitleBar title={"Vehicle Management"} />

            <div
              className="position-fixed top-0 end-0 p-3"
              style={{ zIndex: 1055 }}
            >
              <Toast isOpen={showToast}>
                <ToastHeader
                  icon={toasterType === "danger" ? "danger" : "success"}
                  toggle={() => setShowToast(false)}
                >
                  {toasterType === "danger" ? "Error" : "Success"}
                </ToastHeader>
                <ToastBody>{toastMessage}</ToastBody>
              </Toast>
            </div>

            <div className="vehicleSection">
              <div className="d-flex justify-content-end align-items-end p-2 p-lg-0 gap-1 gap-lg-0">
                <InputGroup className="search-input">
                  <InputGroupText>
                    <i className="ri-search-line" />
                  </InputGroupText>
                  <Input
                    type="text"
                    placeholder="Search input text"
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        dispatch(getAllVehicleList(1, pageSize, searchText));
                        setCurrentPage(1);
                      }
                    }}
                  />
                </InputGroup>
                <Button
                  onClick={() => {
                    setIsAdd(true);
                    toggleAddVehicleModal();
                    dispatch(setLoading({ type: "read", value: false }));
                  }}
                  className="newVehicleBtn d-flex gap-2"
                >
                  <span className="d-none d-lg-flex">Add Vehicle</span>
                  <i className="ri-add-line" />
                </Button>
              </div>

              <div className="trip-table">
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th>Sr.No</th>
                      <th>Vehicle Name</th>
                      <th>Model</th>
                      <th>Vehicle Plate Number</th>
                      <th>Company Ownership</th>
                      <th>Year</th>
                      <th>Alias</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  {loading.read ? (
                    <tbody>
                      <tr>
                        <td colSpan="9" className="text-center align-middle">
                          <Spinner color="primary" />
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody>
                      {vehicleList?.results?.length ? (
                        vehicleList?.results?.map((element, index) => (
                          <tr key={index}>
                            <td>{(currentPage - 1) * pageSize + index + 1}</td>
                            <td>{element.name || "-"}</td>
                            <td>{element.model || "-"}</td>
                            <td>{element.number_plate || "-"}</td>
                            <td>{element.company_ownership || "-"}</td>
                            <td>{element.year || "-"}</td>
                            <td>{element.alias || "-"}</td>
                            <td>
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "80px",
                                  textAlign: "center",
                                  backgroundColor:
                                    element?.status === "active"
                                      ? "#28a74533"
                                      : element?.status === "assigned"
                                        ? "#ffc10733"
                                        : "#dc354533",
                                  padding: "4px 8px",
                                  borderRadius: "10px",
                                  color:
                                    element?.status === "active"
                                      ? "#28a745"
                                      : element?.status === "assigned"
                                        ? "#ffc107"
                                        : "#dc3545",
                                  fontWeight: 500,
                                }}
                              >
                                {element?.status === "active"
                                  ? "Active"
                                  : element?.status === "assigned"
                                    ? "Assigned"
                                    : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <div className="action-icons">
                                <i
                                  className="ri-eye-fill icon"
                                  style={{ color: "#E8A014" }}
                                  onClick={() => {
                                    handleNavigate(element.id);
                                  }}
                                />

                                <i
                                  className="ri-pencil-fill icon"
                                  style={{ color: "#007AFF" }}
                                  onClick={() => {
                                    dispatch(getVehicleById(element.id));
                                    setIsAdd(false);
                                    toggleAddVehicleModal();
                                    dispatch(
                                      setLoading({ type: "read", value: false })
                                    );
                                  }}
                                />
                                <i
                                  className="ri-delete-bin-fill icon"
                                  style={{ color: "#FB0000" }}
                                  onClick={() => {
                                    dispatch(getVehicleById(element.id));
                                    toggleDeleteVehicleModal();
                                    dispatch(
                                      setLoading({ type: "read", value: false })
                                    );
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="text-start text-sm-center" colSpan={9}>
                            <h4>No Vehicle Found</h4>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  )}
                </Table>
              </div>
              {vehicleList?.results?.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  totalItems={vehicleList?.count || 0}
                />
              )}
            </div>
          </div>

          <TripModal
            isModalOpen={addVehicleModal}
            toggleModal={toggleAddVehicleModal}
            buttonText={isAdd ? "Add" : "Update"}
            closeText={"Close without saving"}
            title={"Vehicle Management"}
            showcloseIcon={true}
            tripText={`${isAdd ? "Add " : "Update "}Vehicle`}
            size="lg"
            loading={isAdd ? loading.create : loading.update}
            onSubmit={() => {
              if (formikRef.current) {
                formikRef.current.handleSubmit();
              }
            }}
          >
            <div className="px-3 d-flex flex-column gap-4">
              <Formik
                innerRef={formikRef}
                enableReinitialize
                initialValues={isAdd ? addInitialValues : updateInitialValues}
                // validationSchema={vehicleSchema}
                onSubmit={handleVehicleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        {" "}
                        <FormGroup>
                          <Label for="vehicle_type" className="passwordLabel">
                            Vehicle Type
                          </Label>
                          <Input
                            type="select"
                            name="vehicle_type"
                            className="profileInput"
                            id="vehicle_type"
                            value={values.vehicle_type}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">Select Vehicle Type</option>
                            {vehicleType?.results?.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </Input>
                          {touched.vehicle_type && errors.vehicle_type ? (
                            <FormFeedback type="invalid">
                              {errors.vehicle_type}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label for="name" className="passwordLabel">
                            Vehicle Name
                          </Label>
                          <Input
                            type="text"
                            name="name"
                            className="profileInput"
                            id="name"
                            placeholder="Enter Vehicle Name"
                            value={values.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            invalid={touched.name && errors.name ? true : false}
                          />
                          {touched.name && errors.name ? (
                            <FormFeedback type="invalid">
                              {errors.name}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label for="model" className="passwordLabel">
                            Model
                          </Label>
                          <Input
                            type="text"
                            name="model"
                            className="profileInput"
                            id="model"
                            placeholder="Enter Vehicle Model"
                            value={values.model}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            invalid={
                              touched.model && errors.model ? true : false
                            }
                          />
                          {touched.model && errors.model ? (
                            <FormFeedback type="invalid">
                              {errors.model}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        {" "}
                        <FormGroup>
                          <Label for="number_plate" className="passwordLabel">
                            Vehicle Plate Number
                          </Label>
                          <Input
                            type="text"
                            name="number_plate"
                            className="profileInput"
                            id="number_plate"
                            placeholder="Enter Vehicle Plate Number"
                            value={values.number_plate}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            invalid={
                              touched.number_plate && errors.number_plate
                                ? true
                                : false
                            }
                          />
                          {touched.number_plate && errors.number_plate ? (
                            <FormFeedback type="invalid">
                              {errors.number_plate}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        {" "}
                        <FormGroup>
                          <Label
                            for="company_ownership"
                            className="passwordLabel"
                          >
                            Company Ownership
                          </Label>
                          <Input
                            type="text"
                            name="company_ownership"
                            className="profileInput"
                            id="company_ownership"
                            placeholder="Enter Company Ownership"
                            value={values.company_ownership}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            invalid={
                              touched.company_ownership &&
                                errors.company_ownership
                                ? true
                                : false
                            }
                          />
                          {touched.company_ownership &&
                            errors.company_ownership ? (
                            <FormFeedback type="invalid">
                              {errors.company_ownership}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup aria-autocomplete="none" autoCorrect="off">
                          <Label for="year" className="passwordLabel">
                            Year
                          </Label>
                          <DatePicker
                            id="year"
                            name="year"
                            selected={
                              values.year ? new Date(values.year, 0) : null
                            }
                            onChange={(date) => {
                              const year = date
                                ? date.getFullYear().toString()
                                : "";
                              formikRef.current.setFieldValue("year", year);
                            }}
                            showYearPicker
                            dateFormat="yyyy"
                            className="form-control profileInput w-100"
                            placeholderText="Select year"
                            onBlur={handleBlur}
                            autoComplete="off"
                          />
                          {touched.year && errors.year ? (
                            <FormFeedback type="invalid" className="d-block">
                              {errors.year}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="alias" className="passwordLabel">
                            Alias
                          </Label>
                          <Input
                            type="text"
                            name="alias"
                            className="profileInput"
                            id="alias"
                            placeholder="Enter Alias"
                            value={values.alias}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            invalid={
                              touched.alias && errors.alias ? true : false
                            }
                          />
                          {touched.alias && errors.alias ? (
                            <FormFeedback type="invalid">
                              {errors.alias}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label for="status" className="passwordLabel">
                            Status
                          </Label>
                          <Input
                            type="select"
                            name="status"
                            className="profileInput"
                            id="status"
                            value={values.status}
                            onChange={handleChange}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="assigned">Assigned</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </div>
          </TripModal>

          <DeleteModal
            show={deleteVehicleModal}
            loading={loading.delete}
            onDeleteClick={() => {
              handleDeleteVehicle();
            }}
            onCloseClick={toggleDeleteVehicleModal}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default VehicleManagement;
